import React, { useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useHistory, useParams } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import Card from "../../components/Card";
import Icon from "../../components/Icon";
import {
  closeConversation,
  deleteConversation,
  getConversation,
  listConversations,
  markConversationRead,
  replyConversation,
} from "../../api/conversations.service";
import Modal from "../../components/Modal";
import { fmtDateTime } from "../Campaigns/shared";
import styles from "./Conversations.module.sass";

const STATUS_META = {
  open: { label: "Abierta", className: "status-yellow" },
  answered: { label: "Respondida", className: "status-green" },
  closed: { label: "Cerrada", className: "status-red" },
};

/**
 * Estado REAL del hilo. `status` puede quedarse viejo (hilos migrados, respuestas
 * antiguas), así que manda quién habló el último: si fue el equipo está
 * respondida, si fue el usuario sigue abierta. Cerrada gana siempre.
 */
const statusOf = (thread) => {
  if (thread?.status === "closed") return "closed";
  const last = thread?.messages?.[thread.messages.length - 1];
  return last?.author === "team" ? "answered" : "open";
};

// Dos bandejas: lo que hay que atender y lo ya archivado.
const TABS = [
  { value: "active", label: "Activas" },
  { value: "archived", label: "Archivadas" },
];

const fmtNumber = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "2-digit" }) : "—";

const initials = (s = "") =>
  String(s)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]?.toUpperCase())
    .join("") || "?";

/**
 * CONVERSACIONES de soporte (feedback/bugs como chat user ↔ equipo). Lista de hilos
 * a la izquierda, chat a la derecha. Responder aquí notifica al usuario por push y
 * le aparece en Ajustes → Mis conversaciones de la app.
 */
const Conversations = () => {
  const history = useHistory();
  const { id: selectedId } = useParams();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  // Dentro de Activas: todas / solo sin responder / solo respondidas.
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const chatEndRef = useRef(null);

  const load = async () => {
    try {
      const resp = await listConversations();
      setThreads(Array.isArray(resp?.data) ? resp.data : []);
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudieron cargar las conversaciones" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Cargar el hilo seleccionado (y marcarlo leído para el equipo).
  useEffect(() => {
    if (!selectedId) {
      setThread(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await getConversation(selectedId);
        if (!cancelled && resp?.data?._id) {
          setThread(resp.data);
          if (resp.data.unreadForTeam > 0) {
            markConversationRead(selectedId).then(load);
          }
        }
      } catch (e) {
        if (!cancelled) setToast({ severity: "error", msg: "No se pudo abrir el hilo" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return threads
      .filter((t) => {
        const state = statusOf(t);
        if (tab === "archived" ? state !== "closed" : state === "closed") return false;
        if (statusFilter !== "all" && state !== statusFilter) return false;
        if (
          q &&
          !`${t.subject || ""} ${(t.messages || []).map((m) => m.text).join(" ")}`
            .toLowerCase()
            .includes(q)
        )
          return false;
        return true;
      })
      // Lo que espera respuesta, primero; dentro de cada grupo, lo más reciente.
      .sort((a, b) => {
        const pending = (x) => (statusOf(x) === "open" ? 0 : 1);
        if (pending(a) !== pending(b)) return pending(a) - pending(b);
        return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
      });
  }, [threads, tab, statusFilter, search]);

  const counts = useMemo(() => {
    const acc = { active: 0, archived: 0, open: 0, answered: 0, closed: 0 };
    threads.forEach((t) => {
      const state = statusOf(t);
      acc[state] += 1;
      if (state === "closed") acc.archived += 1;
      else acc.active += 1;
    });
    return acc;
  }, [threads]);

  const handleReply = async () => {
    const text = reply.trim();
    if (!text || !thread) return;
    setSending(true);
    try {
      const resp = await replyConversation(thread._id, text);
      if (resp?.data?._id) {
        setThread(resp.data);
        setReply("");
        setToast({ severity: "success", msg: "Respuesta enviada (+ push al usuario)" });
        load();
      } else {
        setToast({ severity: "error", msg: "No se pudo enviar" });
      }
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudo enviar" });
    } finally {
      setSending(false);
    }
  };

  /** Borrado definitivo: desaparece para el equipo y para el usuario. */
  const handleDelete = async () => {
    if (!thread) return;
    try {
      await deleteConversation(thread._id);
      setConfirmDelete(false);
      setThread(null);
      history.push("/conversations");
      setToast({ severity: "success", msg: "Conversación eliminada" });
      load();
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudo eliminar" });
    }
  };

  const handleClose = async () => {
    if (!thread) return;
    try {
      await closeConversation(thread._id);
      setToast({ severity: "success", msg: "Conversación cerrada" });
      setThread({ ...thread, status: "closed" });
      load();
    } catch (e) {
      setToast({ severity: "error", msg: "No se pudo cerrar" });
    }
  };

  return (
    <>
      <div className={styles.hero}>
        <div>
          <h1 className={styles.heroTitle}>Conversaciones</h1>
          <div className={styles.heroSub}>
            Todo el feedback, como chat. Al responder, al usuario le llega un push que abre este
            mismo hilo en su app.
          </div>
        </div>
      </div>

      <div className={styles.layout}>
        {/* Lista de hilos */}
        <Card className={styles.listCard}>
          <div className={styles.toolbar}>
            <div className={styles.tabs}>
              {TABS.map((t) => (
                <button
                  key={t.value}
                  className={cn(styles.tab, { [styles.tabActive]: tab === t.value })}
                  onClick={() => setTab(t.value)}
                >
                  {t.label}
                  <span className={styles.tabCount}>{counts[t.value]}</span>
                </button>
              ))}
            </div>
            <div className={styles.search}>
              <Icon name="search" size="18" />
              <input
                placeholder="Buscar en asunto y mensajes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {tab === "active" && (
            <div className={styles.filterRow}>
              {[
                { value: "all", label: "Todas" },
                { value: "open", label: `Abiertas · ${counts.open}` },
                { value: "answered", label: `Respondidas · ${counts.answered}` },
              ].map((f) => (
                <button
                  key={f.value}
                  className={cn(styles.filterChip, {
                    [styles.filterChipActive]: statusFilter === f.value,
                  })}
                  onClick={() => setStatusFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className={styles.empty}>Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyEmoji}>💬</div>
              Sin conversaciones aquí.
            </div>
          ) : (
            <div className={styles.threads}>
              {filtered.map((t) => {
                const meta = STATUS_META[statusOf(t)];
                const last = t.messages?.[t.messages.length - 1];
                return (
                  <button
                    key={t._id}
                    className={cn(styles.thread, {
                      [styles.threadActive]: t._id === selectedId,
                      [styles.threadUnread]: t.unreadForTeam > 0,
                    })}
                    onClick={() => history.push(`/conversations/${t._id}`)}
                  >
                    <div className={styles.threadAvatar}>{initials(t.subject)}</div>
                    <div className={styles.threadMain}>
                      <div className={styles.threadTop}>
                        <span className={styles.threadSubject}>{t.subject || "(sin asunto)"}</span>
                        {t.unreadForTeam > 0 && (
                          <span className={styles.unreadBadge}>{t.unreadForTeam}</span>
                        )}
                      </div>
                      {/* El asunto de un feedback ES su primera línea: repetirlo
                          debajo era ruido. Solo se enseña la última respuesta. */}
                      {last && last.text !== t.messages?.[0]?.text && (
                        <div className={styles.threadPreview}>
                          {`${last.author === "team" ? "Tú: " : ""}${last.text}`}
                        </div>
                      )}
                      <div className={styles.threadMeta}>
                        <span className={cn(meta.className, styles.threadStatus)}>{meta.label}</span>
                        <span className={styles.threadSource}>{t.source === "bug" ? "🐞 bug" : "💬 feedback"}</span>
                        {t.removedByUser && <span className={styles.threadRemoved}>borrada por el user</span>}
                        <span className={styles.threadDate}>{fmtDateTime(t.lastMessageAt)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Chat */}
        <Card className={styles.chatCard}>
          {!thread ? (
            <div className={styles.chatEmpty}>
              <div className={styles.emptyEmoji}>👈</div>
              Elige una conversación para verla.
            </div>
          ) : (
            <div className={styles.chat}>
              <div className={styles.chatHead}>
                <div className={styles.chatHeadMain}>
                  <div className={styles.chatTitle}>{thread.subject || "(sin asunto)"}</div>
                  <div className={styles.chatSub}>
                    {thread.source === "bug" ? "🐞 Bug report" : "💬 Feedback"} ·{" "}
                    {thread.messages?.length || 0} mensaje
                    {(thread.messages?.length || 0) === 1 ? "" : "s"} · abierto{" "}
                    {fmtDateTime(thread.createdAt)}
                  </div>
                </div>
                <div className={styles.chatHeadActions}>
                  <span className={cn(STATUS_META[statusOf(thread)].className, styles.chatStatus)}>
                    {STATUS_META[statusOf(thread)].label}
                  </span>
                  {statusOf(thread) !== "closed" && (
                    <button className="button-stroke button-small" onClick={handleClose}>
                      Archivar
                    </button>
                  )}
                  <button
                    className={cn("button-stroke", "button-small", styles.deleteButton)}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              {/* Ficha de quién escribe: atender un hilo sin saber si es premium,
                  qué nivel tiene o cuándo jugó por última vez es atender a ciegas. */}
              <div className={styles.userCard}>
                <div className={styles.userAvatar}>
                  {initials(thread.userInfo?.name || thread.userInfo?.email || "?")}
                </div>
                <div className={styles.userMain}>
                  <div className={styles.userName}>
                    {thread.userInfo?.name || thread.userInfo?.username || "(sin nombre)"}
                    {thread.userInfo?.premium ? (
                      <span className={styles.premiumPill}>
                        ⭐️ {thread.userInfo?.premiumPlan || "PREMIUM"}
                      </span>
                    ) : (
                      <span className={styles.freePill}>Free</span>
                    )}
                    {thread.removedByUser && (
                      <span className={styles.removedPill}>borrada por el user</span>
                    )}
                  </div>
                  <div className={styles.userMail}>{thread.userInfo?.email || "—"}</div>
                </div>
                <div className={styles.userFacts}>
                  {[
                    { k: "Idioma", v: (thread.userInfo?.lang || "—").toUpperCase() },
                    { k: "Nivel", v: thread.userInfo?.level || "—" },
                    { k: "Palabras", v: fmtNumber(thread.userInfo?.knowCards) },
                    { k: "Última partida", v: fmtDate(thread.userInfo?.lastPlayed) },
                    { k: "Alta", v: fmtDate(thread.userInfo?.createdAt) },
                    { k: "App", v: thread.appVersion ? `v${thread.appVersion}` : "—" },
                    { k: "Dispositivo", v: thread.deviceInfo || "—" },
                  ].map((f) => (
                    <div className={styles.fact} key={f.k}>
                      <span className={styles.factKey}>{f.k}</span>
                      <span className={styles.factValue}>{f.v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.messages}>
                {(thread.messages || []).map((m) => (
                  <div
                    key={m._id}
                    className={cn(styles.message, {
                      [styles.messageTeam]: m.author === "team",
                    })}
                  >
                    <div className={styles.bubble}>
                      <div className={styles.bubbleText}>{m.text}</div>
                      <div className={styles.bubbleTime}>{fmtDateTime(m.sentAt)}</div>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {thread.status === "closed" ? (
                <div className={styles.closedNote}>
                  Hilo cerrado. Responder lo reabrirá para el usuario.
                </div>
              ) : null}
              <div className={styles.composer}>
                <textarea
                  className={styles.composerInput}
                  rows={2}
                  placeholder="Escribe la respuesta… (Enter para enviar, Shift+Enter salto de línea)"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleReply();
                    }
                  }}
                />
                <button
                  className={cn("button", styles.sendButton)}
                  disabled={sending || !reply.trim()}
                  onClick={handleReply}
                >
                  {sending ? "…" : "Enviar"}
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Modal visible={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <div className={styles.confirm}>
          <div className={styles.confirmTitle}>¿Eliminar la conversación?</div>
          <div className={styles.confirmBody}>
            Se borra para siempre, también para el usuario. Si solo quieres sacarla de la
            bandeja, usa <strong>Archivar</strong>.
          </div>
          <div className={styles.confirmActions}>
            <button className="button-stroke" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </button>
            <button className={cn("button", styles.buttonDanger)} onClick={handleDelete}>
              Eliminar
            </button>
          </div>
        </div>
      </Modal>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default Conversations;
